import { camelCase, startCase } from 'lodash';
import { DateTime } from 'luxon';
import {
  DESSERT,
  DESSERT_MULTI,
  DRINK,
  HST_TO_UTC_OFFSET,
  KIDS_MAIN,
  KIDS_SNACK,
  LUNCH,
  MAIN,
  MealPlanFoodType,
  MEAL_PLAN_ADD_ON_TITLES,
  MEAL_PLAN_REGIONS_IDS,
  REGION_PRICING_EXPERIMENTS,
  REUSABLE_CLEANING_MEAL_PLAN_CREATION_LAST_GRANDFATHER_DATE,
  REUSABLE_CLEANING_MEAL_PLAN_PRICING_V2_START_DATE,
  REUSABLE_PACKAGING_CLEANING_FEE_BY_MEAL_PLAN_TYPE_SPEC,
  REUSABLE_PACKAGING_CLEANING_FEE_BY_MEAL_PLAN_TYPE_SPEC_V2,
  REUSABLE_PACKAGING_EXCLUDED_HUB_IDS,
  REUSABLE_PACKAGING_REGION_IDS,
  SIDE,
  SNACK,
  STAPLE,
} from './Constants';
import { getDaysOfWeekFromDeliverySchedule, IDayOfWeekDelivery } from './DaysOfWeek';
import { getMealPlanMerchandisingRegionId } from './meal-plan/MerchandisingRegion';
import { pluralize } from './StringUtils';

export const NUM_EXPECTED_FUTURE_ORDERS = 8;

/**
 * Meal plans created on or after this date will have new shefs hidden for first-time users.
 * This prevents breaking users who created meal plans before this behavior was introduced
 * and may have already ordered from new shefs.
 */
export const HIDE_NEW_SHEFS_CUTOFF_DATE = new Date('2026-03-23');

export type MealPlanFoodItemFields = {
  isMainsPlusSides?: boolean | null;
  isFlexibleSides?: boolean | null;
  mealPlanFoodType?: string | null;
};

export const isDateInPausePeriod = (dateToCheck: Date, pauseEndDate: Date | null): boolean => {
  if (!pauseEndDate) {
    return false;
  }

  return DateTime.fromJSDate(dateToCheck) <= DateTime.fromJSDate(pauseEndDate);
};

export const isMealPlanPaused = (pauseEndDate: Date | null): boolean => isDateInPausePeriod(new Date(), pauseEndDate);

/**
 * Gets the numerical delivery day of week for a meal plan.
 * Meal Plan are expected to have exactly one delivery day of week, and there is a lot of code that assumes this.
 *
 * @param obj - Partial<IDayOfWeekDelivery>
 * @returns The numerical delivery day of week (1 = Monday, 7 = Sunday)
 */
export const getDeliveryDOWForMealPlan = <T extends Partial<IDayOfWeekDelivery>>(obj: T): number => {
  const daysOfWeek = getDaysOfWeekFromDeliverySchedule(obj);
  if (daysOfWeek.length !== 1) {
    throw new Error('Meal plan does not have exactly one delivery day of week');
  }
  return daysOfWeek[0];
};

/**
 * Helper function to get a date object a specific number of days prior to the given delivery date
 *
 * @param dateString - delivery date string
 * @param daysToAdjust - number of days prior to delivery date to set
 * @param numAdditionalHoursToSubtract - if we should subtract addional hours from the date
 * @returns
 */
export const getPriorDateAt3pm = (
  dateString: string,
  daysToAdjust: number,
  numAdditionalHoursToSubtract: number
): Date => {
  const parsedDate = DateTime.fromISO(dateString);
  const adjustedDate = parsedDate.minus({ days: daysToAdjust });

  // Set time to 3pm (15:00) and adjust by additional hours if needed
  const finalDateTime = adjustedDate.set({
    hour: 15 - numAdditionalHoursToSubtract,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  return finalDateTime.toJSDate();
};

/**
 * Cutoff time for making adjustments to deliveries is 3pm 3 days prior to nextDeliveryDate
 * If the user visits the site after 3pm, we consider `currentDate` as the next day
 * To ensure that we don't run into issues with date comparisons, we set current time to 2pm
 * and set cutoffDate to 3pm to make sure we don't run into any edge cases
 */
export const getCutoffDate = (
  nextDeliveryDateString: string,
  numAdditionalHoursToSubtract = 0,
  timeZone?: string
): { isPastCutoffDate: boolean; cutoffDate: Date } => {
  // Maintain existing behavior when no timezone is provided (uses server time)
  if (!timeZone) {
    const currentDate = new Date();
    const isAfter3pm = currentDate.getHours() >= 15;
    if (isAfter3pm) {
      currentDate.setDate(currentDate.getDate() + 1);
      currentDate.setHours(14, 0, 0, 0);
    }
    const cutoffDate = getPriorDateAt3pm(nextDeliveryDateString, 3, numAdditionalHoursToSubtract);
    return {
      isPastCutoffDate: currentDate > cutoffDate,
      cutoffDate,
    };
  }

  // Timezone-aware path for use cases where we need local cutoff times
  const now = DateTime.now().setZone(timeZone);
  if (!now.isValid) {
    throw new Error(`Invalid timezone provided: ${timeZone}`);
  }
  const comparisonDate = (now.hour >= 15 ? now.plus({ days: 1 }) : now).set({
    hour: 14,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  const cutoffDateTime = DateTime.fromISO(nextDeliveryDateString, { zone: timeZone })
    .minus({ days: 3 })
    .set({
      hour: 15 - numAdditionalHoursToSubtract,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

  return {
    isPastCutoffDate: comparisonDate.toMillis() > cutoffDateTime.toMillis(),
    cutoffDate: cutoffDateTime.toJSDate(),
  };
};

/**
 * Delivery details (address, time window) for a meal-plan occurrence lock at 4pm the day before
 * delivery, when the occurrence is handed to the delivery service provider. This is later than the
 * 3pm-3-days-prior edit cutoff (see {@link getCutoffDate}) and is the true hard stop for an admin
 * address redirect: past it the change would miss the handoff. Comparison is a literal "is now past
 * the handoff", with no after-3pm next-day rollover.
 */
export const getMealPlanDeliveryDetailsCutoff = (
  nextDeliveryDateString: string,
  timeZone?: string
): { isPastDeliveryDetailsCutoff: boolean; cutoffDate: Date } => {
  const setToHandoff = (dateTime: DateTime): DateTime =>
    dateTime.minus({ days: 1 }).set({ hour: 16, minute: 0, second: 0, millisecond: 0 });

  if (!timeZone) {
    const cutoffDateTime = setToHandoff(DateTime.fromISO(nextDeliveryDateString));
    return {
      isPastDeliveryDetailsCutoff: DateTime.now().toMillis() > cutoffDateTime.toMillis(),
      cutoffDate: cutoffDateTime.toJSDate(),
    };
  }

  const now = DateTime.now().setZone(timeZone);
  if (!now.isValid) {
    throw new Error(`Invalid timezone provided: ${timeZone}`);
  }
  const cutoffDateTime = setToHandoff(DateTime.fromISO(nextDeliveryDateString, { zone: timeZone }));
  return {
    isPastDeliveryDetailsCutoff: now.toMillis() > cutoffDateTime.toMillis(),
    cutoffDate: cutoffDateTime.toJSDate(),
  };
};

/**
 * Calculate the next delivery dates for a given day of the week after (non-inclusive) a specific startDate.
 * The startDate is not necessarily the same weekday, but rather, the date that the new dates should be after.
 * @param params
 * @param params.startDate The initial delivery date to calculate from (non-inclusive)
 * @param params.targetDayOfWeek Numeric day of week (1 = Monday, ..., 7 = Sunday)
 * @param params.numWeeks Number of weeks to calculate dates for (num items in result array)
 * @returns An array of dates for the next `numWeeks` occurrences of the given day of the week, after startDate
 */

interface CalculateDeliveryDaysAfterParams {
  startDate: Date;
  targetDayOfWeek: number;
  numWeeks: number;
}

export function calculateDeliveryDatesAfter({
  startDate,
  targetDayOfWeek,
  numWeeks,
}: CalculateDeliveryDaysAfterParams): Date[] {
  // Standardize the startDate to HST by adjusting its UTC hours
  const standardizedStartDate = new Date(startDate);
  standardizedStartDate.setUTCHours(HST_TO_UTC_OFFSET, 0, 0, 0);

  // UTC, so adding weeks across a daylight-saving change keeps every date at 10:00Z
  const startDateTime = DateTime.fromJSDate(standardizedStartDate, { zone: 'utc' });
  const startDayOfWeek = startDateTime.weekday; // Get the numeric value of the startDate's day of the week

  // Calculate the number of days to add to reach the first occurrence of the target day of the week
  let daysToAdd: number;

  if (targetDayOfWeek === startDayOfWeek) {
    // If the target day is the same as the start day, skip to the next week
    daysToAdd = 7;
  } else if (targetDayOfWeek > startDayOfWeek) {
    // Target day is later in the week
    daysToAdd = targetDayOfWeek - startDayOfWeek;
  } else {
    // Target day is earlier in the week
    daysToAdd = 7 - (startDayOfWeek - targetDayOfWeek);
  }

  // Calculate the first delivery date (always after the startDate)
  const firstDeliveryDate = startDateTime.plus({ days: daysToAdd });

  // Generate the next `numWeeks` delivery dates
  const deliveryDates = Array.from({ length: numWeeks }, (_, index) =>
    firstDeliveryDate.plus({ weeks: index }).toJSDate()
  );

  return deliveryDates;
}

/**
 * Helper function for simple type-only check (used internally by utility functions)
 */
export const isMealPlanSideOrStapleType = (foodType: string | null | undefined): boolean =>
  [SIDE, STAPLE].includes(foodType ?? '');

/**
 * The food type whose section a dish shows in: staples show with sides, and multi-serve desserts
 * with single-serve ones, so customers see one Sides and one Desserts section. Group and title by
 * this; price, pay out and label servings by the stored type.
 */
export const getMealPlanCollectionFoodType = <T extends string | null | undefined>(
  foodType: T
): T | typeof SIDE | typeof DESSERT => {
  if (foodType === STAPLE) return SIDE;
  if (foodType === DESSERT_MULTI) return DESSERT;
  return foodType;
};

export const isMealPlanDessert = (foodType: string | null | undefined): boolean =>
  getMealPlanCollectionFoodType(foodType) === DESSERT;

const MULTI_SERVE_DESSERT_SERVING_LABEL = '4-6 servings';

/** A multi-serve dessert feeds a range the plan doesn't count, so its label is fixed. */
export const getMealPlanServingLabel = ({
  foodType,
  numServings,
  servingSize,
  previousFoodType,
  previousServingSize,
}: {
  foodType: string | null | undefined;
  numServings: number;
  servingSize?: string | null;
  previousFoodType?: string | null;
  previousServingSize?: string | null;
}): string => {
  if (foodType === DESSERT_MULTI) {
    return MULTI_SERVE_DESSERT_SERVING_LABEL;
  }
  const servingCountLabel = `${numServings} ${pluralize('serving', numServings)}`;
  if (previousFoodType === DESSERT_MULTI) {
    return servingSize && servingSize !== MULTI_SERVE_DESSERT_SERVING_LABEL ? servingSize : servingCountLabel;
  }
  if (servingSize) {
    return servingSize;
  }
  if (previousServingSize) {
    return previousServingSize;
  }
  return servingCountLabel;
};

/**
 * Maps a food type string to the corresponding upsell tier `foodItemGroup` key.
 * Main → 'mains', Side → 'sides', all others map 1:1 via camelCase.
 * Returns null if the food type is not provided.
 */
export const getUpsellGroupForFoodType = (foodType: string | null | undefined): string | null => {
  if (!foodType) return null;
  const normalized = camelCase(foodType);
  switch (normalized) {
    case MAIN:
      return 'mains';
    case SIDE:
      return 'sides';
    default:
      return normalized;
  }
};

/**
 * Checks if a food item is a meal plan side dish (side or staple) based on model properties
 * This is the robust version that checks both model properties AND food type
 */
export const isMealPlanSideOrStaple = (foodItem: MealPlanFoodItemFields): boolean =>
  Boolean(foodItem.isMainsPlusSides || foodItem.isFlexibleSides) &&
  isMealPlanSideOrStapleType(foodItem?.mealPlanFoodType ?? null);

/**
 * This helper function will only return true for MealPlanFoodType.Side.
 * MainsPlusSides plans don't have any Side type food items, so this will always
 * return false for those plans.
 */
export const isMealPlanSide = (foodType: MealPlanFoodType): boolean => [SIDE].includes(foodType ?? '');

export const getServingsPerMealByFoodType = (mealPlanFoodType: MealPlanFoodType, numServings: number): number => {
  const isSideDish = isMealPlanSideOrStapleType(mealPlanFoodType);
  const isAddOnDish = isMealPlanAddOnType(mealPlanFoodType);
  const isSingleServing = isSideDish || isAddOnDish;
  return isSingleServing ? 1 : numServings;
};

/**
 * Classifies by meal-plan food type alone, ignoring the listing flags
 * (`isMainsPlusSides`/`isFlexibleSides`) that `isMealPlanAddOn` also requires.
 * A line item in a placed order keeps its classification here even after the
 * shef delists the dish.
 */
export const isMealPlanAddOnType = (foodType: string | null | undefined): boolean =>
  [LUNCH, DRINK, DESSERT, DESSERT_MULTI, SNACK, KIDS_MAIN, KIDS_SNACK].includes(foodType ?? '');

export const isMealPlanAddOn = (foodItem: MealPlanFoodItemFields): boolean =>
  Boolean(foodItem.isMainsPlusSides || foodItem.isFlexibleSides) &&
  isMealPlanAddOnType(foodItem?.mealPlanFoodType ?? null);

export const isMealPlanExtra = (foodItem: MealPlanFoodItemFields): boolean =>
  Boolean(foodItem.isMainsPlusSides || foodItem.isFlexibleSides) &&
  !isMealPlanMainType(foodItem?.mealPlanFoodType ?? null);

/**
 * Classifies by meal-plan food type alone, ignoring the listing flags
 * (`isMainsPlusSides`/`isFlexibleSides`) that `isMealPlanMain` also requires.
 * A line item in a placed order keeps its classification here even after the
 * shef delists the dish.
 */
export const isMealPlanMainType = (foodType: string | null | undefined): boolean => [MAIN].includes(foodType ?? '');

/**
 * Checks if a food item is a meal plan main dish based on model properties
 * This is the robust version that checks both model properties AND food type
 */
export const isMealPlanMain = (foodItem: MealPlanFoodItemFields): boolean =>
  Boolean(foodItem.isMainsPlusSides || foodItem.isFlexibleSides) &&
  isMealPlanMainType(foodItem?.mealPlanFoodType ?? null);

/**
 * A dish is delisted from meal plans when it can no longer be sold on one:
 * flexible sides was turned off after purchase (all meal plans are
 * FLEXIBLE_SIDES format), or its food type no longer maps to a main,
 * side/staple, or add-on. Line items referencing a delisted dish still exist
 * in placed orders; display surfaces hide them and count the order as missing
 * those units.
 */
export const isDelistedFromMealPlans = (foodItem: MealPlanFoodItemFields): boolean =>
  !foodItem.isFlexibleSides ||
  !(
    isMealPlanMainType(foodItem.mealPlanFoodType) ||
    isMealPlanSideOrStapleType(foodItem.mealPlanFoodType) ||
    isMealPlanAddOnType(foodItem.mealPlanFoodType)
  );

/**
 * The quantity a line item counts as on a meal plan. Sides, staples and add-ons count by quantity;
 * a main's servings are divided by the plan's servings to count meals.
 *
 * @param mealPlanFoodType - the line item's food type, which decides how its quantity counts.
 * @param lineItemQuantity - the line item's quantity, in servings for mains.
 * @param numServings - the plan's servings per meal.
 * @param isFlexServings - flex plans sell mains by the serving, so a flex main's quantity is
 *   returned as-is. Leave it false for counts compared against the plan's meals per week.
 * @returns meals for a control main, otherwise the line item's quantity.
 */
export const getSelectedUnitsFromLineItemByFoodType = (
  mealPlanFoodType: MealPlanFoodType,
  lineItemQuantity: number,
  numServings: number,
  isFlexServings = false
): number => {
  if (
    isMealPlanSideOrStapleType(mealPlanFoodType) ||
    isMealPlanAddOnType(mealPlanFoodType) ||
    (isFlexServings && isMealPlanMainType(mealPlanFoodType))
  ) {
    return lineItemQuantity;
  }

  return lineItemQuantity / numServings;
};

/**
 * Normalizes various enum string forms (e.g., 'KidsMain', 'KIDS_MAIN', 'kidsMain') to camelCase keys
 * that match the keys used in MEAL_PLAN_ADD_ON_TITLES.
 */
const toCamelKey = (input: string | null | undefined): string => {
  if (!input) return '';
  return camelCase(String(input));
};

/**
 * Start case for camel/underscore/hyphen strings via lodash's startCase
 */
const startCaseFromCamelLike = (camel: string): string => {
  if (!camel) return '';
  return startCase(camel);
};

/** Normalize a string to a loose key for comparisons: remove separators and lowercase */
const toLooseKey = (s: string): string => s.replace(/[_\-\s]+/g, '').toLowerCase();

/**
 * Returns the display section title for a meal plan food type, choosing curated add-on titles when applicable
 * and pluralizing based on the provided unitsCount.
 *
 * Examples:
 * - ('KidsMain', 1) => 'Kids Meal'
 * - ('KidsMain', 3) => 'Kids Meals'
 * - ('Lunch', 2) => 'Lunches'
 * - ('Main', 4) => 'Mains'
 */
export const getMealPlanSectionTitle = (foodType: string | null | undefined, unitsCount: number): string => {
  const key = getMealPlanCollectionFoodType(toCamelKey(foodType));
  // Robust, case-insensitive, separator-insensitive lookup against curated add-on titles
  const curatedTitle = Object.entries(MEAL_PLAN_ADD_ON_TITLES).find(([k]) => toLooseKey(k) === toLooseKey(key))?.[1];
  if (curatedTitle) {
    const lower = curatedTitle.toLowerCase();
    if (unitsCount === 1) {
      if (lower === 'lunch') return 'Lunch';
      return curatedTitle.endsWith('s') ? curatedTitle.slice(0, -1) : curatedTitle;
    }
    // plural case
    if (lower === 'lunch') return 'Lunches';
    return curatedTitle; // other curated titles are already plural forms
  }
  const base = startCaseFromCamelLike(key);

  if (!base) return '';
  return pluralize(base, unitsCount);
};

/**
 * This needs to be kept in sync with the MealPlanFormat enum defined in shef-api/src/meal-plan/MealPlan.graphql
 * in the event that we add a new format with new pricing structures
 */

export enum MealPlanFormat {
  FlexibleSides = 'FLEXIBLE_SIDES',
  MainsPlusSides = 'MAINS_PLUS_SIDES',
}

/**
 * Determines if meal plans are supported for a given region and delivery hub combination.
 * Meal plans require both:
 * 1. The region to be in the list of supported meal plan regions
 * 2. The zip code to have a delivery hub ID (indicating delivery infrastructure)
 *
 * @param regionId - The region ID to check
 * @param deliveryHubId - The delivery hub ID to check (can be null/undefined)
 * @returns true if meal plans are supported, false otherwise
 */
export const supportsMealPlans = ({
  regionId,
  deliveryHubId,
}: {
  regionId: number | null | undefined;
  deliveryHubId: string | number | null | undefined;
}): boolean => MEAL_PLAN_REGIONS_IDS.includes(regionId ?? 0) && Boolean(deliveryHubId);

/**
 * Some delivery hubs inside an otherwise reusable-enabled region can't run the container
 * return/wash loop, so reusable packaging is withheld there. Hub ids arrive as strings from the
 * cached-zip path and as numbers from the meal plan fragment, hence the coercion.
 */
export const isReusablePackagingExcludedHub = (deliveryHubId: string | number | null | undefined): boolean =>
  deliveryHubId != null && REUSABLE_PACKAGING_EXCLUDED_HUB_IDS.includes(Number(deliveryHubId));

export const supportsReusablePackaging = ({
  regionId,
  deliveryHubId,
}: {
  regionId: number | null | undefined;
  deliveryHubId: string | number | null | undefined;
}): boolean =>
  regionId != null &&
  REUSABLE_PACKAGING_REGION_IDS.includes(regionId) &&
  !isReusablePackagingExcludedHub(deliveryHubId);

/** Allow either local enum or GraphQL string enum values */
export type AnyMealPlanFormat = MealPlanFormat | string | null | undefined;

/**
 * 2026-02-24: Regional configs are not reliable for meal plans because we don't use last cached zip code.
 * They are hydrated using `currentUser.lastSearchedZipCode` which is not updated when the user changes their zip code.
 * This is a temporary solution to enable Add-Ons for a few regions until we can implement a better solution.
 *
 * 2026-03-02: Added all current WSS supported regions to the list.
 */
const ADD_ON_REGION_IDS = [1, 2, 5, 6, 7, 8, 9, 10];
const isAddOnRegion = (regionId: number): boolean =>
  ADD_ON_REGION_IDS.includes(getMealPlanMerchandisingRegionId(regionId));
/**
 * Centralized check for whether meal plan Add-Ons should be enabled in a given context.
 * Conditions:
 *  - Feature flag is ON
 *  - Meal plan format is Flexible Sides
 *  - User is in a supported meal plan region AND has a delivery hub (via supportsMealPlans)
 */
export const areMealPlanAddOnsEnabled = (params: {
  featureFlagOn: boolean | null | undefined;
  regionId: number | null | undefined;
  deliveryHubId: string | number | null | undefined;
}): boolean => {
  const { featureFlagOn, regionId, deliveryHubId } = params;
  return Boolean(featureFlagOn) && supportsMealPlans({ regionId, deliveryHubId }) && isAddOnRegion(regionId ?? 0);
};

/**
 * Legacy alias for backward compatibility
 * @deprecated Use supportsMealPlans instead for clearer naming
 */
export const isInMealPlanRegion = supportsMealPlans;

/**
 * Calculates the reusable packaging cleaning fee for a group order.
 * Uses a lookup table to return the fee for the specific combination of main units and servings.
 *
 * If the meal plan type is not found in the lookup table, the default fee
 * is calculated at $1 per container, capped at $12.00 total fee.
 *
 * If the customer opted into reusable packaging before the grandfathered date, the fee is 0.
 *
 * @param numMainUnits - Number of main entrees
 * @param numMainServings - Number of servings per main entree
 * @param optedInAt - When the customer opted into reusable packaging (DateTime)
 * @returns Cleaning fee in cents
 */
export const calculateReusablePackagingCleaningFee = ({
  numMainUnits,
  numMainServings,
  optedInAt,
}: {
  numMainUnits: number;
  numMainServings: number;
  optedInAt: DateTime;
}): number => {
  if (optedInAt <= REUSABLE_CLEANING_MEAL_PLAN_CREATION_LAST_GRANDFATHER_DATE) {
    return 0;
  }

  const mealPlanTypeKey = `${numMainUnits}x${numMainServings}`;
  const defaultFeeCalculation = Math.max(numMainUnits * numMainServings * 100, 1200);

  if (optedInAt.toJSDate() <= REUSABLE_CLEANING_MEAL_PLAN_PRICING_V2_START_DATE) {
    return REUSABLE_PACKAGING_CLEANING_FEE_BY_MEAL_PLAN_TYPE_SPEC[mealPlanTypeKey] ?? defaultFeeCalculation;
  }

  return REUSABLE_PACKAGING_CLEANING_FEE_BY_MEAL_PLAN_TYPE_SPEC_V2[mealPlanTypeKey] ?? defaultFeeCalculation;
};

/**
 * Resolves a meal plan type's price-per-serving in cents, falling back to
 * pricePerUnit / numServings (rounded) when the server didn't populate the
 * field. Backend's MealPlan resolver returns this field today; the fallback
 * is defensive for older clients and cached fragments missing it.
 *
 * Returns 0 when numServings is 0 to avoid Infinity/NaN propagating into UI.
 */
export const getPricePerServingCents = (mealPlanType: {
  pricePerServing?: number | null;
  pricePerUnit: number;
  numServings: number;
}): number =>
  mealPlanType.pricePerServing ??
  (mealPlanType.numServings > 0 ? Math.round(mealPlanType.pricePerUnit / mealPlanType.numServings) : 0);

/**
 * Checks if a meal plan has a cleaning fee exemption (grandfathered in).
 * Plans created before the grandfather date are exempt from the cleaning fee.
 *
 * @param isReusablePackagingPlan - Whether the plan uses reusable packaging
 * @param mealPlanCreatedAt - The date the meal plan was created (DateTime)
 * @returns True if the plan is exempt from cleaning fees
 */
export const hasReusableCleaningFeeExemption = ({
  isReusablePackagingPlan,
  mealPlanCreatedAt,
}: {
  isReusablePackagingPlan: boolean;
  mealPlanCreatedAt: DateTime;
}): boolean =>
  isReusablePackagingPlan && mealPlanCreatedAt <= REUSABLE_CLEANING_MEAL_PLAN_CREATION_LAST_GRANDFATHER_DATE;

/**
 * Checks if a meal plan has legacy pricing (lower than current experiment pricing).
 * Compares the user's current price per serving against the region's control pricing.
 *
 * @param pricePerUnit - The meal plan's price per unit (in cents)
 * @param numServings - Number of servings per unit
 * @param regionId - The region ID for the meal plan
 * @returns True if the user has legacy pricing lower than current default
 */
export const hasLegacyPricing = ({
  pricePerUnit,
  numServings,
  regionId,
}: {
  pricePerUnit: number;
  numServings: number;
  regionId?: number | null;
}): boolean => {
  if (!regionId || !pricePerUnit || !numServings) {
    return false;
  }

  const regionPricingConfig = REGION_PRICING_EXPERIMENTS[regionId];
  if (!regionPricingConfig) {
    return false;
  }

  const userPricePerServing = pricePerUnit / numServings;
  return userPricePerServing < regionPricingConfig.controlPricePerServing;
};
